import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthContext } from '@/contexts/auth-context';
import { userStore } from '@/models/stores/index';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, Check, Slack } from 'lucide-react';
import { observer } from 'mobx-react-lite';

export const Route = createFileRoute('/(authenticated)/connections/')({
  component: AccountPageWrapper,
});

const SLACK_OAUTH_BASE_URL = 'https://slack.com/oauth/v2/authorize';
const SLACK_CLIENT_ID = import.meta.env.VITE_PADDOCK_SLACK_CLIENT_ID;
const SLACK_SCOPES = [
  'users.profile:read',
  'users:read',
  'im:read',
  'im:write',
];

const AccountPage = observer(() => {
  const { user: authUser } = useAuthContext();

  const user = userStore.getById(authUser?.id as string);

  const origin =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : '';
  const REDIRECT_URI = origin + '/api/auth/callback/slack';

  const state = {
    redirect_uri: REDIRECT_URI,
  };

  const encodedState = btoa(JSON.stringify(state));

  const handleConnect = () => {
    const params = {
      client_id: SLACK_CLIENT_ID as string,
      scope: SLACK_SCOPES.join(','),
      redirect_uri: REDIRECT_URI,
      state: encodedState,
    };

    const authUrl =
      SLACK_OAUTH_BASE_URL + '?' + new URLSearchParams(params).toString();

    window.open(authUrl);
  };

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto p-6 dark:bg-dark h-full">
      <div className="mb-8">
        <h1 className="text-2xl  mb-2">Connected accounts</h1>
        <p className="text-muted-foreground">
          You can connect your account with other apps and services
        </p>
      </div>

      {/* Slack Integration */}
      <div className="mb-10">
        <div className="flex items-center mb-2">
          <h2 className="text-lg">Slack</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Connect Slack with your account for real-time updates, issue creation,
          and seamless progress sharing
        </p>

        <div className="border rounded-lg p-4 mb-2">
          <div className="flex items-start">
            <div className="mr-3 mt-1">
              <div className="bg-[#4A154B] p-1.5 rounded">
                <Slack className="h-4 w-4 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm">
                    Slack account{' '}
                    {user?.slack_connected ? 'connected' : 'not connected'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {user?.slack_connected
                      ? 'Your Slack account is linked and you can receive personal notifications'
                      : 'Your Slack account is not linked and you will not receive personal notifications'}
                  </p>
                </div>
                <div className="flex items-center">
                  {user?.slack_connected ? (
                    <Badge
                      variant="outline"
                      className="text-xs bg-green-50 text-green-700 border-green-200 flex items-center gap-1"
                    >
                      <Check className="h-3 w-3" /> Connected
                    </Badge>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={handleConnect}
                    >
                      Connect <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function AccountPageWrapper() {
  return <AccountPage />;
}
